import { renderDocument, type ArticleDoc } from '@archetelos/editor';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer, FilterTabs, NotesField, StatusBadge } from '@/features/admin/ui';
import { PlayerSurface } from '@/features/watch/PlayerSurface';
import { useAuth } from '@/lib/auth/useAuth';
import { fetchPreviewPlayback } from '@/lib/playbackToken';
import { adminQueries, auditLog, notify, type Content } from '@/lib/queries/admin';
import { useAssetMap } from '@/lib/queries/assets';
import { contentQueries } from '@/lib/queries/content';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/admin/content')({
  component: ContentReviewPage,
});

type Row = Content & {
  creator: {
    display_name: string | null;
    username: string | null;
    avatar_url: string | null;
    creator_status: string;
  } | null;
};

const FILTERS = [
  { value: 'pending_review', label: 'Pending review' },
  { value: 'published', label: 'Approved' },
  { value: 'sentback', label: 'Send-back' },
  { value: 'all', label: 'All' },
] as const;

function ContentReviewPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['value']>('pending_review');
  const [active, setActive] = useState<Row | null>(null);
  const [notes, setNotes] = useState('');

  const queryStatus = filter === 'all' || filter === 'sentback' ? null : filter;
  const list = useQuery(adminQueries.contentQueue(queryStatus));
  let rows = (list.data ?? []) as Row[];
  if (filter === 'sentback') {
    rows = rows.filter((r) => r.status === 'draft' && Boolean(r.reviewer_notes));
  }

  const rate = useQuery(adminQueries.creatorApprovalRate(active?.creator_id));
  // Body is no longer on the list row (revoke); fetch it for the open item via
  // the owner/admin RPC.
  const activeBody = useQuery(contentQueries.byId(active?.id));
  const assetMap = useAssetMap((activeBody.data?.body_json as ArticleDoc | null) ?? null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'content'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'picks'] });
  };

  const review = useMutation({
    mutationFn: async ({
      row,
      action,
    }: {
      row: Row;
      action: 'approve' | 'send_back' | 'reject';
    }) => {
      if ((action === 'send_back' || action === 'reject') && !notes.trim()) {
        throw new Error('Reviewer notes are required for send-back and reject.');
      }
      const nextStatus =
        action === 'approve' ? 'published' : action === 'send_back' ? 'draft' : 'rejected';
      const { error } = await supabase
        .from('content')
        .update({
          status: nextStatus,
          reviewer_id: user?.id,
          reviewer_notes: notes.trim() || null,
        })
        .eq('id', row.id);
      if (error) throw error;

      const { error: revErr } = await supabase.from('content_reviews').insert({
        content_id: row.id,
        reviewer_id: user!.id,
        action,
        notes: notes.trim() || null,
      });
      if (revErr) throw revErr;

      await auditLog(`content.${action}`, 'content', row.id, { title: row.title });

      const name = row.creator?.display_name ?? null;
      if (action === 'approve') {
        await notify('content_published', null, {
          name,
          contentTitle: row.title,
          contentUrl: `${import.meta.env.PUBLIC_MARKETING_URL ?? ''}/${
            row.type === 'video' ? 'video' : 'article'
          }/${row.slug}`,
        });
      } else if (action === 'send_back') {
        await notify('content_sent_back', null, {
          name,
          contentTitle: row.title,
          reviewerNotes: notes.trim(),
          editorUrl: `${window.location.origin}/creator/content/${row.id}`,
        });
      } else {
        await notify('content_rejected', null, {
          name,
          contentTitle: row.title,
          reviewerNotes: notes.trim(),
        });
      }
    },
    onSuccess: (_d, v) => {
      toast.success(
        v.action === 'approve'
          ? 'Published.'
          : v.action === 'send_back'
            ? 'Sent back to the contributor.'
            : 'Rejected.',
      );
      setActive(null);
      setNotes('');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Review failed.'),
  });

  const togglePick = useMutation({
    mutationFn: async (row: Row) => {
      const next = !row.is_editors_pick;
      const { error } = await supabase
        .from('content')
        .update({
          is_editors_pick: next,
          editors_pick_at: next ? new Date().toISOString() : null,
          editors_pick_by: next ? user?.id : null,
        })
        .eq('id', row.id);
      if (error) throw error;
      await auditLog(next ? 'content.pick_set' : 'content.pick_unset', 'content', row.id, {
        title: row.title,
      });
    },
    onSuccess: () => {
      toast.success('Editor’s pick updated.');
      setActive(null);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Toggle failed.'),
  });

  // Direct publish/unpublish (admin VOD curation — e.g. live recordings, which
  // import as unpublished). Publishing is gated by validate_content_creator
  // (creator must be an approved host).
  const setStatus = useMutation({
    mutationFn: async ({ row, next }: { row: Row; next: 'published' | 'unpublished' }) => {
      const { error } = await supabase.from('content').update({ status: next }).eq('id', row.id);
      if (error) throw error;
      await auditLog(`content.${next}`, 'content', row.id, { title: row.title });
    },
    onSuccess: (_d, v) => {
      toast.success(v.next === 'published' ? 'Published.' : 'Unpublished.');
      setActive(null);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed.'),
  });

  // Admin delete — content_delete_admin RLS permits deleting any piece; the row
  // and its version history (ON DELETE CASCADE) go permanently.
  const remove = useMutation({
    mutationFn: async (row: Row) => {
      const { error } = await supabase.from('content').delete().eq('id', row.id);
      if (error) throw error;
      await auditLog('content.deleted', 'content', row.id, { title: row.title, type: row.type });
    },
    onSuccess: () => {
      toast.success('Deleted.');
      setActive(null);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Review" title="Content Review" />
      <div className="mt-6">
        <FilterTabs options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Nothing in this queue.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => {
                  setActive(r);
                  setNotes(r.reviewer_notes ?? '');
                }}
                className="block w-full rounded-md border border-ink/10 bg-bg-light/40 p-4 text-left hover:border-oxblood/40"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-serif text-base text-ink">
                    {r.is_editors_pick && (
                      <Star className="mr-1 inline h-3.5 w-3.5 fill-oxblood text-oxblood" />
                    )}
                    {r.title}
                  </p>
                  <span className="flex items-center gap-2">
                    <span className="font-sans text-[0.7rem] uppercase tracking-[0.12em] text-ink/40">
                      {r.type}
                    </span>
                    <StatusBadge status={r.status} />
                  </span>
                </div>
                <p className="mt-1 font-sans text-xs text-ink/50">
                  {r.creator?.display_name ?? 'Unknown'} · submitted{' '}
                  {new Date(r.updated_at).toLocaleDateString()}
                </p>
                {r.excerpt && (
                  <p className="mt-2 line-clamp-2 font-sans text-sm text-ink/70">{r.excerpt}</p>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.title ?? 'Content'}
      >
        {active && (
          <div className="space-y-5">
            <p className="font-sans text-xs text-ink/50">
              {active.creator?.display_name} ·{' '}
              {rate.data ? `${rate.data.published}/${rate.data.total} prior pieces published` : '—'}
            </p>

            {active.type === 'video' && active.video_bunny_id ? (
              <AdminVideoPreview contentId={active.id} title={active.title} />
            ) : (
              <article
                className="prose max-w-none font-serif text-ink"
                // Rendered through the SAME renderDocument path readers see.
                dangerouslySetInnerHTML={{
                  __html: renderDocument(
                    (activeBody.data?.body_json as ArticleDoc | null) ?? null,
                    assetMap,
                  ),
                }}
              />
            )}

            <div className="space-y-4 border-t border-ink/10 pt-5">
              {active.status === 'published' ? (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={togglePick.isPending}
                    onClick={() => togglePick.mutate(active)}
                    className="flex items-center gap-2 rounded-md border border-ink/20 px-4 py-2 font-sans text-sm text-ink hover:border-oxblood disabled:opacity-60"
                  >
                    <Star
                      className={`h-4 w-4 ${
                        active.is_editors_pick ? 'fill-oxblood text-oxblood' : ''
                      }`}
                    />
                    {active.is_editors_pick ? "Remove editor's pick" : "Mark editor's pick"}
                  </button>
                  <button
                    type="button"
                    disabled={setStatus.isPending}
                    onClick={() => setStatus.mutate({ row: active, next: 'unpublished' })}
                    className="rounded-md border border-ink/30 px-4 py-2 font-sans text-sm font-medium text-ink hover:border-oxblood disabled:opacity-60"
                  >
                    Unpublish
                  </button>
                </div>
              ) : active.status === 'unpublished' ? (
                <button
                  type="button"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ row: active, next: 'published' })}
                  className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
                >
                  Publish
                </button>
              ) : (
                <>
                  <NotesField value={notes} onChange={setNotes} />
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      disabled={review.isPending}
                      onClick={() => review.mutate({ row: active, action: 'approve' })}
                      className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={review.isPending}
                      onClick={() => review.mutate({ row: active, action: 'send_back' })}
                      className="rounded-md border border-ink/30 px-4 py-2 font-sans text-sm font-medium text-ink hover:border-oxblood disabled:opacity-60"
                    >
                      Send back
                    </button>
                    <button
                      type="button"
                      disabled={review.isPending}
                      onClick={() => review.mutate({ row: active, action: 'reject' })}
                      className="rounded-md border border-oxblood px-4 py-2 font-sans text-sm font-medium text-oxblood hover:bg-oxblood/10 disabled:opacity-60"
                    >
                      Reject
                    </button>
                  </div>
                  <p className="font-sans text-xs text-ink/50">
                    Notes are required for send-back and reject, and are sent to the contributor
                    verbatim.
                  </p>
                </>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-ink/10 pt-4">
                <p className="font-sans text-xs text-ink/50">
                  Deletes the piece and its version history permanently.
                </p>
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete “${active.title}”? This permanently removes it and its version history and cannot be undone.`,
                      )
                    ) {
                      remove.mutate(active);
                    }
                  }}
                  className="shrink-0 rounded-md border border-signal px-4 py-2 font-sans text-sm font-medium text-signal hover:bg-signal/10 disabled:opacity-60"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// Token-fetched embed (Spec 20 P8-B1, third adoption site). Token Auth requires
// a signed URL; admins are authorized on the preview branch's content path.
function AdminVideoPreview({ contentId, title }: { contentId: string; title: string }) {
  const playback = useQuery({
    queryKey: ['preview-playback', 'content', contentId] as const,
    queryFn: () => fetchPreviewPlayback({ contentId }),
  });
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md bg-ink">
      {playback.data ? (
        <PlayerSurface playback={playback.data} title={title} />
      ) : (
        <div className="flex h-full items-center justify-center font-sans text-sm text-video-text-faint">
          {playback.isLoading ? 'Loading preview…' : 'Preview unavailable.'}
        </div>
      )}
    </div>
  );
}
